<template>
  <div>
    <ul v-if="!isPreview" class="tab-nav">
      <li
        v-for="(section, i) in sectionList"
        :key="i"
        class="tab-nav-item"
        :class="{ active: activeSectionName === section.name }"
        @click="scrollToRef(section.name)"
      >
        {{ section.text }}
      </li>
    </ul>
    <div class="p-3 bg-white border-top border-bottom border-gray-03">
      <h2 class="ribbon-head" ref="orderOverview">仕事概要</h2>

      <div class="py-3">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          仕事詳細
        </h3>
        <div
          class="order-detail-text-wrapper"
          :class="expandedClasses"
          data-testid="order-detail-text-wrapper"
        >
          <p
            ref="orderDetailText"
            class="mt-3 text-size-14 text-mild text-break text-pre-wrap order-detail-text"
            v-html="linkifyOrderDescription"
          />
          <button
            v-if="expandDescriptionEnabled && !isDescriptionExpanded"
            class="w-100 text-center expand-button"
            data-testid="expand-button"
            @click="expandDescription"
          >
            <span class="ml-2">さらに表示</span>
            <FeatherIcon icon="chevron-down" class="mr-1" />
          </button>
        </div>
      </div>

      <h2
        class="ribbon-head mt-3"
        id="applicationCondition"
        ref="applicationCondition"
      >
        申込条件
      </h2>

      <div class="wrapper">
        <div>
          <div class="py-3">
            <h3 class="text-size-14 text-weight-500 text-primary-dark">
              業務に関する書類
            </h3>
            <button
              class="btn-order mt-2"
              @click="$emit('click-notice-of-employment')"
            >
              労働条件通知書を確認
            </button>
            <button
              v-if="order.attachmentFile"
              class="btn-order mt-2"
              @click="$emit('click-attachment-file')"
            >
              その他の書類を確認
            </button>
          </div>

          <div class="py-3">
            <div class="d-flex justify-content-between">
              <h3 class="text-size-14 text-weight-500 text-primary-dark">
                資格条件
              </h3>
              <RouterLink
                v-if="isLogin || isPreview"
                :to="{ name: 'MypageQualificationList' }"
                tag="div"
                class="text-size-14 text-blue"
                :style="isPreview ? 'pointer-events: none' : null"
              >
                資格の登録を更新
              </RouterLink>
            </div>
            <ul class="mt-2">
              <li
                v-if="order.allowUnqualified"
                data-testid="qualification-none"
                class="tag-outline mr-2 mb-2"
              >
                なし
              </li>
              <li
                v-for="qualification in order.qualifications"
                :key="qualification.id"
                :data-testid="`qualification-${qualification.id}`"
                class="tag-outline mr-2 mb-2"
                :class="{
                  'tag-outline--yellow': hasQualification(qualification.id)
                }"
              >
                {{ qualification.name }}
              </li>
            </ul>

            <slot
              v-if="
                !order ||
                  (!order.allowUnqualified &&
                    (!order.qualifications ||
                      order.qualifications.length === 0))
              "
              name="unsetQualifications"
            />
          </div>

          <div class="py-3">
            <h3 class="text-size-14 text-weight-500 text-primary-dark">
              経験・スキル
            </h3>
            <ul class="mt-2" v-if="otherConditionList.length > 0">
              <li
                v-for="(otherCondition, index) in otherConditionList"
                :key="index"
                class="list-text text-size-14 text-mild"
              >
                ・{{ otherCondition }}
              </li>
            </ul>
            <div v-else class="text-size-14 text-mild">
              指定なし
            </div>
            <slot
              v-if="otherConditionList.length === 0"
              name="unsetOtherConditionList"
            />
          </div>

          <div class="py-3">
            <h3 class="text-size-14 text-weight-500 text-primary-dark">
              服装・身だしなみ
            </h3>
            <ul class="mt-2" v-if="clothesConditionList.length > 0">
              <li
                v-for="(clothesCondition, index) in clothesConditionList"
                :key="index"
                class="list-text text-size-14 text-mild"
              >
                ・{{ clothesCondition }}
              </li>
            </ul>
            <div v-else class="text-size-14 text-mild">
              指定なし
            </div>

            <slot
              v-if="clothesConditionList.length === 0"
              name="unsetClothesConditionList"
            />
          </div>

          <div class="py-3">
            <h3 class="text-size-14 text-weight-500 text-primary-dark">
              服装写真
            </h3>
            <ul class="row align-items-center mt-2 mx-n1">
              <li
                v-for="(clothesSample, index) in clothesSampleList"
                :key="index"
                class="col-4 px-1"
              >
                <div>
                  <a
                    :href="getOptimizedImageUrl(clothesSample.image)"
                    target="_blank"
                  >
                    <img
                      :src="getOptimizedImageUrl(clothesSample.image)"
                      alt=""
                      class="w-100"
                    />
                  </a>
                </div>
              </li>
            </ul>
            <ul class="row align-items-start mt-2 mx-n1">
              <li
                v-for="(clothesSample, index) in clothesSampleList"
                :key="index"
                class="col-4 px-1 text-size-12 text-mild text-center"
              >
                {{ clothesSample.comment }}
              </li>
            </ul>

            <slot
              v-if="clothesSampleList.length === 0"
              name="unsetClothesSampleList"
            />
          </div>

          <div class="py-3">
            <h3 class="text-size-14 text-weight-500 text-primary-dark">
              持ち物・その他の条件
            </h3>
            <ul class="mt-2" v-if="belongingsList.length > 0">
              <li
                v-for="(belongings, index) in belongingsList"
                :key="index"
                class="list-text text-size-14 text-mild"
              >
                ・{{ belongings }}
              </li>
            </ul>
            <div v-else class="text-size-14 text-mild">
              指定なし
            </div>
          </div>
          <slot v-if="belongingsList.length === 0" name="unsetBelongingsList" />
        </div>
      </div>

      <h2 class="ribbon-head mt-3" ref="officeInfo">事業所情報</h2>

      <div class="pt-3 pb-2">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          法人名
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ getOfficeProp("corporationName") }}
        </div>
      </div>

      <div class="pt-3 pb-2">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          事業所名
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ getOfficeProp("name") }}
        </div>
      </div>

      <div class="pt-3 pb-2">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          連絡先
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ getOfficeProp("phoneNumber") }}
        </div>
      </div>

      <div class="py-2">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          住所
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ getOfficeProp("address") }}
        </div>
        <ConfirmRoute
          :latitude="getOfficeProp('latitude')"
          :longitude="getOfficeProp('longitude')"
          :mapStyle="isPreview ? 'pointer-events:none' : null"
          @confirm-route="$emit('click-view-root')"
        />
      </div>

      <div class="py-2">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          アクセス
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ getOfficeProp("access") }}
        </div>
      </div>

      <div class="py-2">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          可能な通勤手段
        </h3>
        <CommuteMethodTags
          class="mt-1"
          :canCommuteByCar="getOfficeProp('canCommuteByCar')"
          :canCommuteByBike="getOfficeProp('canCommuteByBike')"
          :canCommuteByBicycle="getOfficeProp('canCommuteByBicycle')"
          :canCommuteByPublicTransit="
            getOfficeProp('canCommuteByPublicTransit')
          "
        />
      </div>

      <div class="py-2" v-if="isShowingParkingType" data-testid="parking-type">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          駐車場
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ parkingTypeText }}
        </div>
      </div>

      <div class="py-2" v-if="getOfficeProp('commuteMethodNote')">
        <h3 class="text-size-14 text-weight-500 text-primary-dark">
          通勤手段の備考
        </h3>
        <div class="mt-1 text-size-14 text-mild">
          {{ getOfficeProp("commuteMethodNote") }}
        </div>
      </div>

      <h2 class="ribbon-head mt-3" ref="review">レビュー</h2>

      <div class="mb-2 py-3">
        <h3 class="mb-1 text-size-14 text-weight-500 text-primary-dark">
          総合評価
        </h3>
        <EvaluationWithoutCount
          v-if="
            evaluationCount !== undefined &&
              evaluationTotalAverage !== undefined
          "
          :value="evaluationTotalAverage"
          :count="evaluationCount"
          size="24"
          textSize="16"
        />
      </div>
      <div class="mb-2">
        <div
          :key="evaluation.id"
          v-for="evaluation in filteredAnonymousEvaluations"
          class="mb-0"
        >
          <h3
            class="d-flex align-items-center mb-1 text-size-14 text-weight-500 text-primary-dark"
          >
            <div>
              {{ evaluation.roundedAge }}代
              {{ getSexLabel(evaluation.sex) }}
            </div>
            <div class="tag-outline ml-2">
              {{ getQualificationName(evaluation.qualificationId) }}
            </div>
            <i
              v-if="evaluation.isFeatured"
              class="ml-2 material-symbols-outlined pin-icon"
              data-testid="pin-icon"
              >keep</i
            >
            <div
              class="ml-auto text-gray-05 text-size-14"
              data-testid="reviewed-at"
            >
              {{
                evaluation.monthsAgo > 0
                  ? `${evaluation.monthsAgo}ヶ月前`
                  : "1ヶ月以内"
              }}
            </div>
          </h3>
          <AppLabel variant="success" class="mr-2">良い点 </AppLabel>
          <div class="text-size-14 text-mild">
            <ReviewComment :comment="evaluation.goodPoint" :maxLength="150" />
          </div>
          <AppLabel variant="mild" class="mr-2">改善点</AppLabel>
          <div class="text-size-14 text-mild">
            <ReviewComment :comment="evaluation.badPoint" :maxLength="150" />
          </div>
          <div class="my-2"><hr /></div>
        </div>
        <div>
          <button
            v-if="readOldestReview"
            class="btn-order mt-2"
            @click="showNextAnonymousEvaluation"
            data-testid="read-more-reviews"
          >
            <FeatherIcon icon="chevron-down" class="mr-1" />
            さらにレビューを表示する（残り{{
              remainingAnonymousEvaluationCount
            }}件）
          </button>
          <div
            v-else-if="
              anonymousEvaluations.length >= upperLimitOfAnonymousEvaluation
            "
            class="d-flex justify-content-center text-gray-05 text-size-14"
            data-testid="read-fetched-reviews"
          >
            コメントは最新の{{
              upperLimitOfAnonymousEvaluation
            }}件のみが表示されています
          </div>
        </div>
        <div v-if="order.openedAt" class="update-date" data-testid="opened-at">
          掲載日：{{ formattedOpenedAt }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import AppButton from "@/components/atoms/button/AppButton.vue";
import AppLabel from "@/components/atoms/input/AppLabel.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { Component, Prop, Vue } from "vue-facing-decorator";
import EvaluationWithoutCount from "@/components/molecules/other/EvaluationWithoutCount.vue";
import ReviewComment from "@/components/molecules/other/ReviewComment.vue";
import ConfirmRoute from "@/components/organisms/common/ConfirmRoute.vue";
import CommuteMethodTags from "@/components/molecules/orders/CommuteMethodTags.vue";
import { authStore } from "@/store/auth";
import { scrollTo } from "@/utils/scrollUtil";
import { getSafeAreaTop } from "@/utils/domUtil";

import { FavoriteOffice } from "@/types/office/FavoriteOffice";
import { OrderDetail } from "@/types/order/Order";
import { Office } from "@/types/office/Office";
import { AnonymousEvaluation } from "@/types/order/AnonymousEvaluation";
import { getOptimizedImageUrl } from "@/utils/image";
import { masterStore } from "@/store/master";
import { gender } from "@/models/gender";
import { linkifyText } from "@/utils/stringUtil";
import { formatFromISOString } from "@/utils/date";
import { PARKING_TYPE } from "@/const/parkingType";

const SCROLL_OFFSET = -90;
const UPPER_LIMIT_OF_ANONYMOUS_EVALUATIONS = 100;
const REVIEWS_BATCH_SIZE = 10;

// ステータスバー等の高さ
const safeAreaTop = getSafeAreaTop();

@Component({
  components: {
    AppButton,
    AppLabel,
    CommuteMethodTags,
    FeatherIcon,
    EvaluationWithoutCount,
    ConfirmRoute,
    ReviewComment
  }
})
export default class OrderTabContents extends Vue {
  @Prop({ type: Object, required: true })
  order!: OrderDetail;
  @Prop({ type: Number, required: true })
  evaluationCount!: number;
  @Prop({ type: Number, required: false })
  evaluationTotalAverage!: number | null;

  @Prop({
    type: Object || null, // FavoriteOffice
    default: null
  })
  favoriteOffice!: FavoriteOffice;

  @Prop({
    type: Array,
    default: () => []
  })
  anonymousEvaluations!: AnonymousEvaluation[];

  @Prop({
    type: Boolean,
    default: false
  })
  isLogin!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  isPreview!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  forceExpandDescriptionDisabled!: boolean;

  // 評価の表示件数
  anonymousEvaluationShowingCount = 3;

  isScrollingToAnchor = false;

  // 仕事詳細を展開可能にするか
  expandDescriptionEnabled = false;
  // 展開可能な仕事詳細が展開済みか
  isDescriptionExpanded = false;

  sectionList = [
    {
      name: "orderOverview",
      text: "仕事概要"
    },
    {
      name: "applicationCondition",
      text: "申込条件"
    },
    {
      name: "officeInfo",
      text: "事業所情報"
    },
    {
      name: "review",
      text: "レビュー"
    }
  ];

  activeSectionName = "";

  get filteredAnonymousEvaluations() {
    return this.anonymousEvaluations.slice(
      0,
      this.anonymousEvaluationShowingCount
    );
  }

  get orderDescription() {
    if (this.isPreview && !this.order.description) return "未設定";
    return this.order.description;
  }

  get linkifyOrderDescription() {
    return linkifyText(this.orderDescription);
  }

  get isFavorite() {
    return (
      this.favoriteOffice !== undefined &&
      this.favoriteOffice !== null &&
      this.favoriteOffice.isFavorite
    );
  }

  get otherConditionList() {
    return this.createGroupList(this.order, "otherCondition", 5);
  }

  get clothesConditionList() {
    return this.createGroupList(this.order, "clothesCondition", 5);
  }

  get clothesSampleList() {
    const images = this.createGroupList(
      this.order.office,
      "clothesSampleImage",
      3
    );
    const comments = this.createGroupList(
      this.order.office,
      "clothesSampleComment",
      3
    );
    return images.map((image, index) => {
      return {
        image,
        comment: comments[index]
      };
    });
  }

  get belongingsList() {
    return this.createGroupList(this.order, "belongings", 5);
  }

  get upperLimitOfAnonymousEvaluation() {
    return UPPER_LIMIT_OF_ANONYMOUS_EVALUATIONS;
  }

  get remainingAnonymousEvaluationCount() {
    if (
      this.anonymousEvaluations.length > UPPER_LIMIT_OF_ANONYMOUS_EVALUATIONS
    ) {
      return (
        UPPER_LIMIT_OF_ANONYMOUS_EVALUATIONS -
        this.anonymousEvaluationShowingCount
      );
    }
    return (
      this.anonymousEvaluations.length - this.anonymousEvaluationShowingCount
    );
  }

  get readOldestReview() {
    return (
      this.anonymousEvaluations.length > this.anonymousEvaluationShowingCount &&
      this.upperLimitOfAnonymousEvaluation >
        this.anonymousEvaluationShowingCount
    );
  }

  get expandedClasses() {
    return {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "enable-expand": this.expandDescriptionEnabled,
      expanded: this.isDescriptionExpanded
    };
  }

  get formattedOpenedAt() {
    // この関数が呼び出されるのは order.openedAt が設定されている場合のみ
    return formatFromISOString(this.order.openedAt!, "yyyy年MM月dd日");
  }

  get parkingTypeText() {
    const parkingType = Object.values(PARKING_TYPE).find(
      parkingType => parkingType.value === this.order.office?.parkingType
    );
    if (!parkingType) {
      return null;
    }
    return parkingType.text;
  }

  get canCommuteByCarOrBike() {
    return (
      this.order.office?.canCommuteByCar || this.order.office?.canCommuteByBike
    );
  }

  get isShowingParkingType() {
    return this.canCommuteByCarOrBike && !!this.parkingTypeText;
  }

  getSexLabel(sexValue: string) {
    return gender[sexValue];
  }

  getQualificationName(qualificationId: number) {
    return masterStore.qualificationMap.get(qualificationId)!.name;
  }

  getOptimizedImageUrl(image: string | null | undefined) {
    const url = getOptimizedImageUrl(image || null, 240, null);
    return url ?? undefined;
  }

  getOfficeProp(prop: keyof Office) {
    if (!this.order.office) return null;
    return this.order.office[prop];
  }

  scrollToRef(refName: string, offset = SCROLL_OFFSET) {
    const target = this.$refs[refName] as HTMLElement;
    if (target) {
      this.isScrollingToAnchor = true;
      scrollTo(window, target, { offset, duration: 500 });
      setTimeout(() => {
        this.isScrollingToAnchor = false;
        this.activeSectionName = refName;
      }, 550);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createGroupList(object: any, property: string, num: number) {
    return [...Array(num)]
      .map((_, i) => {
        if (object === null) return;
        return object[`${property}${i + 1}`];
      })
      .filter(Boolean);
  }

  hasQualification(qualificationId: number) {
    if (!authStore.existToken) return false;
    return authStore.user?.qualifications.some(
      q => q.qualification === qualificationId && q.status === "verified"
    );
  }

  setCurrentSection() {
    if (this.isPreview) return;

    // タブを押して移動する場合は発動させない
    if (this.isScrollingToAnchor) return;

    // コンテンツエリアが上部にくるまではタブを強調しない
    const firstSectionEl = this.$refs[this.sectionList[0].name] as
      | HTMLElement
      | undefined;
    if (!firstSectionEl) return;
    if (
      0 <
      firstSectionEl.getBoundingClientRect().top + SCROLL_OFFSET - safeAreaTop
    ) {
      this.activeSectionName = "";
      return;
    }

    // 表示領域の上部がセクション内にあればタブ強調
    // NOTE: 下から順にチェックするために、reverseしている
    for (const section of [...this.sectionList].reverse()) {
      const sectionEl = this.$refs[section.name] as HTMLElement | undefined;
      if (!sectionEl) return;
      const sectionTopPosition = sectionEl.getBoundingClientRect().top;
      if (0 >= Math.floor(sectionTopPosition + SCROLL_OFFSET - safeAreaTop)) {
        this.activeSectionName = section.name;
        break;
      }
    }
  }

  mounted() {
    window.addEventListener("resize", this.setCurrentSection);
    window.addEventListener("scroll", this.setCurrentSection);

    // 仕事詳細の「申込条件・持ち物確認」から案件詳細に移動した際、「申込条件」にスクロールする。
    const hash = this.$route.hash;
    if (hash) {
      const refName = hash.substr(1);
      const target = this.$refs[refName] as HTMLElement;
      if (target) {
        window.scrollTo(0, target.offsetTop + SCROLL_OFFSET);
      }
    }

    if (!this.forceExpandDescriptionDisabled) {
      // $refs はリアクティブではないため、コンポーネントマウント時に都度算出する
      this.expandDescriptionEnabled =
        (this.$refs.orderDetailText as HTMLDivElement)?.clientHeight > 180;
    }
  }

  beforeUnmount() {
    window.removeEventListener("resize", this.setCurrentSection, false);
    window.removeEventListener("scroll", this.setCurrentSection, false);
  }

  showNextAnonymousEvaluation() {
    if (
      this.anonymousEvaluationShowingCount + REVIEWS_BATCH_SIZE >
      this.upperLimitOfAnonymousEvaluation
    ) {
      this.anonymousEvaluationShowingCount = this.upperLimitOfAnonymousEvaluation;
    } else {
      this.anonymousEvaluationShowingCount += REVIEWS_BATCH_SIZE;
    }
  }

  expandDescription() {
    this.isDescriptionExpanded = true;
  }
}
</script>

<style lang="scss" scoped>
.line-clamp-9 {
  display: -webkit-box;
  -webkit-line-clamp: 9;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.list-text {
  padding-left: 1em;
  text-indent: -1em;
}

.more-view-button {
  width: 100%;
  padding: 4px;
  font-size: 14px;
  text-align: center;
  background-color: $white;
  border: 1px solid $gray-03;
  border-radius: 4px;
}

.review-text {
  padding-left: 1.3em;
  text-indent: -1.3em;
}

.ribbon-head {
  position: relative;
  display: inline-block;
  height: 24px;
  padding: 0 16px;
  margin-left: -16px;
  font-weight: 500;
  color: $white;
  background-color: $blue;

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    width: 0;
    height: 0;
    content: "";
    border-color: transparent #fff transparent transparent;
    border-style: solid;
    border-width: 12px 5px 12px 0;
  }
}

.tab-nav {
  position: fixed;
  top: calc(env(safe-area-inset-top, 0px) + 49px);
  left: 0;
  z-index: 100;
  width: 100%;
  background-color: $white;
  border-bottom: 1px solid $gray-03;
}

.tab-nav-item {
  display: inline-block;
  width: 25%;
  padding-top: 10px;
  padding-bottom: 8px;
  font-size: 14px;
  line-height: 20px;
  color: $mild;
  text-align: center;
  border-bottom: 2px solid $white;

  &.active {
    color: $blue;
    border-bottom-color: $blue;
  }
}

.tag-outline {
  display: inline-block;
  padding: 2px 8px;
  font-size: 14px;
  color: $gray-05;
  background-color: $gray-01;
  border: 1px solid $gray-03;
  border-radius: 4px;

  &--yellow {
    color: $primary-dark;
    background-color: $light-yellow;
    border-color: $yellow;
  }
}

.wrapper {
  position: relative;
}

:deep(.order-detail-text a) {
  color: $blue;
}

.order-detail-text-wrapper {
  position: relative;

  &.enable-expand {
    .expand-button {
      position: absolute;
      bottom: 0;
      padding: 8px 0;
      background-color: $white;
      box-shadow: 0 -30px 30px 20px $white;
    }

    &:not(.expanded) {
      max-height: 180px;
      padding-bottom: 0;
      overflow-y: hidden;
    }
  }
}

.update-date {
  display: flex;
  flex-direction: row-reverse;
  font-size: 14px;
}

.pin-icon {
  transform: rotate(45deg);
}
</style>
